import { useQueryClient } from "@tanstack/react-query";
import {
  useAddFundToWatchlist,
  useRemoveFundFromWatchlist,
  useWatchlist,
} from "creso-core/hooks";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Button, cn } from "web-design-system";

// Watchlist toggle for a funds table row: shown on row hover, and always
// once the fund is in the watchlist
export function WatchlistStarCell({ fundId }: { fundId: string }) {
  const queryClient = useQueryClient();
  const { data: watchlistData } = useWatchlist();
  const addMutation = useAddFundToWatchlist();
  const removeMutation = useRemoveFundFromWatchlist();

  const isAdded = !!watchlistData?.funds?.some((f) => f.fund_id === fundId);
  const isPending = addMutation.isPending || removeMutation.isPending;

  const toggle = () => {
    const onSuccess = (message: string) => () => {
      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ["watchlist"] });
    };
    if (isAdded) {
      removeMutation.mutate(
        { fund_ids: [fundId] },
        {
          onSuccess: onSuccess("Removed from favourites"),
          onError: () => toast.error("Failed to remove from favourites"),
        }
      );
    } else {
      addMutation.mutate(
        { fund_ids: [fundId] },
        {
          onSuccess: onSuccess("Added to favourites"),
          onError: () => toast.error("Failed to add to favourites"),
        }
      );
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      disabled={isPending}
      aria-label={isAdded ? "Remove from favourites" : "Add to favourites"}
      aria-pressed={isAdded}
      onClick={(e) => {
        e.stopPropagation();
        toggle();
      }}
      className={cn(
        "size-8 opacity-0 focus-visible:opacity-100 [tr:hover_&]:opacity-100",
        isAdded && "opacity-100"
      )}
    >
      <Star
        className={cn(
          "size-4 text-muted-foreground",
          isAdded && "text-primary fill-primary"
        )}
      />
    </Button>
  );
}
